import React, { useCallback, useEffect, useState } from "react";
import BingoTopBox from "./BingoTopBox";
import { BingoGame } from "bingo/types/bingo-game.model";
import { bingoGameService } from "services/services";
import { useUpdatePlayerMutation } from "quiz/api/players.endpoints";
import { PlayerInfoState } from "../views/types/bingo.game.view.types";
import { removeZeros } from "bingo/utils/removeZeros";
import { Modal, Button } from "@mantine/core";
import type { ScreenType } from "../views/types/bingo.game.view.types";
export interface BingoGridProps {
  numbers: number[]; // grid numbers
  isPreviewMode?: boolean;
  topText?: string;
  bingoGame: BingoGame;
  playerId: string;
  partitionId: string;
  entries?: number[];
  refetch?: () => void;
  setIsCheckingTap?: (value: boolean) => void;
  onWin: () => void;
  playerInfoState?: PlayerInfoState;
  setEndScreenMessage?: React.Dispatch<React.SetStateAction<string>>;
  setCurrentScreen?: React.Dispatch<React.SetStateAction<ScreenType>>;
}

export const BingoGrid: React.FC<BingoGridProps> = ({
  numbers,
  isPreviewMode = false,
  topText,
  bingoGame,
  playerId,
  partitionId,
  entries = [], // Set default empty array
  refetch,
  setIsCheckingTap,
  onWin,
  playerInfoState,
  setEndScreenMessage,
  setCurrentScreen,
}) => {
  const [wrong, setWrong] = useState<number | null>(null);
  const [drawnNumbers, setDrawnNumbers] = useState<number[]>([]);
  const [drawStarted] = useState<boolean>(true);
  const [updatePlayer] = useUpdatePlayerMutation();

  const [showBingoButton, setShowBingoButton] = useState<boolean>(false);
  const [showNumberNotDrawnMessage, setShowNumberNotDrawnMessage] =
    useState<boolean>(false);
  const [alreadyWinnerModalOpen, setAlreadyWinnerModalOpen] =
    useState<boolean>(false);

  const PSG_GOLD = "#D4AF37";
  const PSG_BLUE = "#004170";
  const PSG_RED = "#e30613";
  const CORRECT_SELECTION_COLOR =
    bingoGame.bingoTemplateSnapshot?.privateScreenSettings
      ?.gridNumberSuccessBgColor || PSG_BLUE;
  const INCORRECT_SELECTION_COLOR =
    bingoGame.bingoTemplateSnapshot?.privateScreenSettings
      ?.gridNumberFailureBgColor || PSG_RED;
  const gridSize = 3;

  const gameAlreadyHasWinnerModal = () => {
    setAlreadyWinnerModalOpen(true);
  };

  //This function sets the bingo grid in checkerboard pattern
  const makeBingoCheckerboardPattern = useCallback((index: number): boolean => {
    const row = Math.floor(index / 3);
    const col = index % 3;
    return (row + col) % 2 === 0;
  }, []);

  const redirectToEndScreen = () => {
    setAlreadyWinnerModalOpen(false);
    setCurrentScreen!("P4");
  };
  const tapNumber = async (
    num: number,
    isClickableBingoNumber: boolean,
  ): Promise<void> => {
    if (
      isPreviewMode ||
      !drawStarted ||
      entries?.includes(num) ||
      !isClickableBingoNumber
    )
      return;

    try {
      setIsCheckingTap!(true);
      // Get latest drawn numbers first
      const data = await bingoGameService.getDrawNumberslist(bingoGame.id);

      //check if game has a winner
      if (data.winnerInfo) {
        gameAlreadyHasWinnerModal();
        setIsCheckingTap!(false);

        if (bingoGame.bingoTemplateSnapshot.frontOfficeMessages.looserPrivate) {
          console.log("loser message server if 1");
          setEndScreenMessage!(
            bingoGame.bingoTemplateSnapshot.frontOfficeMessages.looserPrivate,
          );
        } else {
          console.log("loser message server else 1");
          setEndScreenMessage!("Game already has a winner! ");
        }
        return;
      }

      //check if game is closed
      if (data.status === "CLOSED") {
        console.log("game closed");
        gameAlreadyHasWinnerModal();
        setIsCheckingTap!(false);
        return;
      }

      const latestDrawnNumbers = data.drawNumbersList;

      setDrawnNumbers(latestDrawnNumbers);

      // Check against latest drawn numbers
      if (latestDrawnNumbers.includes(num)) {
        // Create new array with the number added
        const newSelected = [...entries, num];

        //check winner and so something
        //create object and updare player payload state

        const gridPayload: PlayerInfoState = {
          ...playerInfoState,
          bingoGridNumbers: removeZeros(numbers),
          bingoEntries: newSelected,
          bingoGameId: bingoGame.id,
        };

        console.log("new selected", newSelected);
        console.log("numbers", numbers);
        console.log("grid player info state", gridPayload);

        // Update server with the new array
        await updatePlayer({
          ...gridPayload,
        } as any).unwrap();

        // AFTER the server update completes, trigger refetch
        if (refetch) {
          setIsCheckingTap!(false);
          await refetch();
        }

        setWrong(null);
      } else {
        setIsCheckingTap!(false);
        setWrong(num);
        setShowNumberNotDrawnMessage(true);
        setTimeout(() => {
          setWrong(null);
          setShowNumberNotDrawnMessage(false);
        }, 2000);
      }
    } catch (error) {
      console.error("Error in tapNumber:", error);
      setIsCheckingTap!(false);
    }
  };
  //check winner
  const checkWinner = useCallback(() => {
    const allClickableNumbers = numbers.filter((_, index) =>
      makeBingoCheckerboardPattern(index),
    );
    if (
      allClickableNumbers.every(
        (num) => entries.includes(num) && drawnNumbers.includes(num),
      )
    ) {
      onWin();
    }
  }, [numbers, entries, onWin, makeBingoCheckerboardPattern, drawnNumbers]);

  useEffect(() => {
    const initializeDrawnNumbers = async () => {
      try {
        const data = await bingoGameService.getDrawNumberslist(bingoGame.id);
        setDrawnNumbers(data.drawNumbersList);
      } catch (error) {
        console.error("Failed to initialize drawn numbers:", error);
      }
    };

    initializeDrawnNumbers();
  }, [bingoGame.id]);

  //Screen Setup
  useEffect(() => {
    if (isPreviewMode) return;

    const allClickableNumbers = numbers.filter((_, index) =>
      makeBingoCheckerboardPattern(index),
    );
    if (
      allClickableNumbers.every(
        (num) => entries.includes(num) && drawnNumbers.includes(num),
      )
    ) {
      checkWinner();
    } else {
      setShowBingoButton(false);
    }
  }, [
    checkWinner,
    entries,
    drawnNumbers,
    numbers,
    makeBingoCheckerboardPattern,
    isPreviewMode,
  ]);

  return (
    <div className="flex flex-col items-center gap-3 px-8 py-6 -mt-12 w-full max-w-md">
      <BingoTopBox
        isPreviewMode={isPreviewMode}
        bingoGame={bingoGame}
        showNumberNotDrawnMessage={showNumberNotDrawnMessage}
        showBingoButton={showBingoButton}
        handleBingoClick={checkWinner}
        topText={
          bingoGame.bingoTemplateSnapshot?.frontOfficeMessages.gridChoice +
          " - " +
          topText
        }
      />

      {/* Bingo Grid */}
      <div
        className=" p-4 w-full"
        style={{
          backgroundColor:
            bingoGame.bingoTemplateSnapshot.privateScreenSettings.gridBgColor,
        }}
      >
        <div
          className="grid gap-2 w-full"
          style={{
            gridTemplateColumns: `repeat(${gridSize}, 1fr)`,
            gridAutoRows: "1fr",
          }}
        >
          {numbers.map((num, index) => {
            const isClickableBingoNumber = makeBingoCheckerboardPattern(index);
            let bgColor =
              bingoGame.bingoTemplateSnapshot.privateScreenSettings.gridBgColor;
            if (isPreviewMode) {
              bgColor = isClickableBingoNumber
                ? bingoGame.bingoTemplateSnapshot.privateScreenSettings
                    .gridNumberStandardColor || PSG_GOLD
                : bingoGame.bingoTemplateSnapshot.privateScreenSettings
                    .gridBgColor;
            } else {
              if (entries.includes(num)) {
                bgColor = drawnNumbers.includes(num)
                  ? CORRECT_SELECTION_COLOR
                  : INCORRECT_SELECTION_COLOR;
              } else if (wrong === num) {
                bgColor = INCORRECT_SELECTION_COLOR;
              } else if (isClickableBingoNumber) {
                bgColor =
                  bingoGame.bingoTemplateSnapshot.privateScreenSettings
                    .gridNumberStandardColor || PSG_GOLD;
              }
            }

            return (
              <div
                key={index}
                className="relative"
                style={{ paddingBottom: "100%" }}
              >
                <div
                  className={`absolute inset-0 flex items-center justify-center text-6xl font-bold ${
                    !isPreviewMode ? "cursor-pointer" : ""
                  }`}
                  style={{
                    backgroundColor: bgColor,
                    color:
                      bingoGame.bingoTemplateSnapshot.privateScreenSettings
                        .gridNumberColor,
                  }}
                  onClick={() => tapNumber(num, isClickableBingoNumber)}
                >
                  {isClickableBingoNumber ? num : ""}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Winner Modal */}
      <Modal
        opened={alreadyWinnerModalOpen}
        onClose={() => setAlreadyWinnerModalOpen(false)}
        title={bingoGame.bingoTemplateSnapshot.frontOfficeMessages.gameEnded}
        centered
        withCloseButton={false}
        closeOnClickOutside={false}
        closeOnEscape={false}
      >
        <div className="flex flex-col items-center">
          <p className="mb-4">
            {bingoGame.bingoTemplateSnapshot.frontOfficeMessages.looserPrivate}
          </p>
          <Button
            className="bg-button-admin"
            style={{
              backgroundColor:
                bingoGame?.bingoTemplateSnapshot?.privateScreenSettings
                  ?.buttonBackgroundColor,
              color:
                bingoGame?.bingoTemplateSnapshot?.privateScreenSettings
                  ?.buttonsLabelColor,
              borderColor:
                bingoGame?.bingoTemplateSnapshot?.privateScreenSettings
                  ?.buttonsBorderColor,
            }}
            fullWidth
            onClick={redirectToEndScreen}
          >
            {
              bingoGame.bingoTemplateSnapshot.frontOfficeMessages
                .welcomeReturnButtonLabel
            }
          </Button>
        </div>
      </Modal>
    </div>
  );
};

export default BingoGrid;
